/**
 * Custom Hooks for Task and Quadrant Management
 * Encapsulates all state management and business logic
 */
import { useState, useCallback, useEffect } from 'react';

const QUADRANT_CONFIG = [
  { id: 0, title: 'Urgent - Do It Now', defaultColor: 'color-red' },
  { id: 1, title: 'Urgent - Today', defaultColor: 'color-orange' },
  { id: 2, title: 'High Priority - Long time', defaultColor: 'color-green' },
  { id: 3, title: 'Personal Notes', defaultColor: 'color-blue' },
];

const DEFAULT_DATA = QUADRANT_CONFIG.map((config) => ({
  id: config.id,
  title: config.title,
  tasks: [],
  color: config.defaultColor,
}));

/**
 * Initializes quadrants from localStorage or returns default data
 */
function useQuadrants() {
  const [quadrants, setQuadrants] = useState(() => {
    const saved = localStorage.getItem('tm-matrix-data');
    return saved ? JSON.parse(saved) : DEFAULT_DATA;
  });

  useEffect(() => {
    localStorage.setItem('tm-matrix-data', JSON.stringify(quadrants));
  }, [quadrants]);

  return [quadrants, setQuadrants];
}

/**
 * Initializes theme preference from localStorage
 */
function useDarkMode() {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('tm-matrix-theme');
    return savedTheme === 'dark';
  });

  useEffect(() => {
    localStorage.setItem('tm-matrix-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  return [isDarkMode, setIsDarkMode];
}

/**
 * Adds a task to a specific quadrant
 */
function useAddTask(setQuadrants) {
  return useCallback(
    (quadrantId, taskText) => {
      const trimmedText = taskText.trim();
      if (!trimmedText) return;

      setQuadrants((prev) =>
        prev.map((q) => {
          if (q.id === quadrantId) {
            const newTask = {
              id: crypto.randomUUID(),
              text: trimmedText,
              completed: false,
              createdAt: new Date().toISOString(),
            };
            return {
              ...q,
              tasks: [...(q.tasks || []), newTask],
            };
          }
          return q;
        })
      );
    },
    [setQuadrants]
  );
}

/**
 * Toggles task completion status
 */
function useToggleTask(setQuadrants) {
  return useCallback(
    (quadrantId, taskId) => {
      setQuadrants((prev) =>
        prev.map((q) =>
          q.id === quadrantId
            ? {
                ...q,
                tasks: q.tasks.map((t) => {
                  if (t.id !== taskId) return t;
                  const isNowCompleted = !t.completed;
                  return {
                    ...t,
                    completed: isNowCompleted,
                    completedAt: isNowCompleted ? new Date().toISOString() : null,
                  };
                }),
              }
            : q
        )
      );
    },
    [setQuadrants]
  );
}

/**
 * Removes a task from a quadrant
 */
function useRemoveTask(setQuadrants) {
  return useCallback(
    (quadrantId, taskId) => {
      setQuadrants((prev) =>
        prev.map((q) =>
          q.id === quadrantId ? { ...q, tasks: q.tasks.filter((t) => t.id !== taskId) } : q
        )
      );
    },
    [setQuadrants]
  );
}

/**
 * Moves a task between quadrants or reorders within same quadrant
 */
function useMoveTask(setQuadrants) {
  return useCallback(
    (fromQuadrantId, fromTaskId, toQuadrantId, toIndex) => {
      setQuadrants((prev) => {
        const fromQuadrant = prev.find((q) => q.id === fromQuadrantId);
        if (!fromQuadrant) return prev;

        const task = fromQuadrant.tasks.find((t) => t.id === fromTaskId);
        if (!task) return prev;

        // Same quadrant - reorder within
        if (fromQuadrantId === toQuadrantId) {
          const tasks = [...fromQuadrant.tasks];
          const fromIndex = tasks.findIndex((t) => t.id === fromTaskId);
          tasks.splice(fromIndex, 1);
          tasks.splice(toIndex > fromIndex ? toIndex - 1 : toIndex, 0, task);
          return prev.map((q) => (q.id === fromQuadrantId ? { ...q, tasks } : q));
        }

        // Different quadrants
        const toQuadrant = prev.find((q) => q.id === toQuadrantId);
        if (!toQuadrant) return prev;

        const newFromTasks = fromQuadrant.tasks.filter((t) => t.id !== fromTaskId);
        const newToTasks = [...toQuadrant.tasks];
        newToTasks.splice(toIndex, 0, task);

        return prev.map((q) => {
          if (q.id === fromQuadrantId) return { ...q, tasks: newFromTasks };
          if (q.id === toQuadrantId) return { ...q, tasks: newToTasks };
          return q;
        });
      });
    },
    [setQuadrants]
  );
}

/**
 * Updates quadrant properties (title, color, etc.)
 */
function useUpdateQuadrant(setQuadrants) {
  return useCallback(
    (id, field, value) => {
      setQuadrants((prev) =>
        prev.map((q) => (q.id === id ? { ...q, [field]: value } : q))
      );
    },
    [setQuadrants]
  );
}

/**
 * Toggles a task's plannedDate for today
 */
function useTogglePlanToday(setQuadrants) {
  return useCallback(
    (quadrantId, taskId) => {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      setQuadrants((prev) =>
        prev.map((q) =>
          q.id === quadrantId
            ? {
                ...q,
                tasks: q.tasks.map((t) =>
                  t.id === taskId
                    ? {
                        ...t,
                        plannedDate: t.plannedDate === todayStr ? null : todayStr,
                      }
                    : t
                ),
              }
            : q
        )
      );
    },
    [setQuadrants]
  );
}

export {
  QUADRANT_CONFIG,
  DEFAULT_DATA,
  useQuadrants,
  useDarkMode,
  useAddTask,
  useToggleTask,
  useRemoveTask,
  useMoveTask,
  useUpdateQuadrant,
  useTogglePlanToday,
};
